import { useState, useMemo } from 'react';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { apiClient } from '@/services/api';
import SearchBar from '@/components/SearchBar';
import FilterDropdown from '@/components/FilterDropdown';
import type {
  ContextType,
  Restaurant,
  RestaurantGroup,
  Role,
  RoleAssignment,
  User,
  UserCreateRequest,
  UserRoleAssignment,
  UserUpdateRequest,
} from '@/types/api';
import { Plus, Edit2, Trash2, X, Mail, Phone, Shield, RotateCcw, AlertTriangle, RefreshCw } from 'lucide-react';
import { useHasPermission } from '@/hooks/usePermission';
import { PERM } from '@/constants/permissions';
import { useAuthStore } from '@/stores/authStore';

const formatRoleLabel = (role: Pick<Role, 'name' | 'displayName'>): string =>
  role.displayName?.trim() || role.name.replace(/^ROLE_/, '').replace(/_/g, ' ');

/**
 * Render a role assignment as `RoleName · ScopeName`. Falls back to the
 * bare role label for PLATFORM context, since there's no scope to show.
 */
const formatAssignmentLabel = (a: UserRoleAssignment): string => {
  const role = formatRoleLabel(a);
  const scope = a.restaurantName ?? a.restaurantGroupName ?? null;
  return scope ? `${role} · ${scope}` : role;
};

// Full IANA zone list, same source RestaurantModal's curated list is drawn
// from -- using the runtime's own data instead of hand-maintaining another
// copy. Falls back to a single entry on engines without the API (not yet in
// this project's lib target, hence the cast).
const supportedValuesOf = (Intl as { supportedValuesOf?: (key: string) => string[] })
  .supportedValuesOf;
const TIMEZONE_OPTIONS: string[] = supportedValuesOf ? supportedValuesOf('timeZone') : ['UTC'];

// No canonical "list every locale" API exists, so this is a curated set of
// BCP-47 tags (UserCreateRequest/UserUpdateRequest cap locale at 10 chars).
const LOCALE_OPTIONS: { value: string; label: string }[] = [
  { value: 'en-US', label: 'English (US)' },
  { value: 'en-GB', label: 'English (UK)' },
  { value: 'fr-FR', label: 'French' },
  { value: 'de-DE', label: 'German' },
  { value: 'es-ES', label: 'Spanish' },
  { value: 'it-IT', label: 'Italian' },
  { value: 'pt-PT', label: 'Portuguese' },
  { value: 'pt-BR', label: 'Portuguese (Brazil)' },
  { value: 'nl-NL', label: 'Dutch' },
  { value: 'ro-RO', label: 'Romanian' },
  { value: 'pl-PL', label: 'Polish' },
  { value: 'tr-TR', label: 'Turkish' },
  { value: 'ar-SA', label: 'Arabic' },
  { value: 'zh-CN', label: 'Chinese (Simplified)' },
  { value: 'ja-JP', label: 'Japanese' },
  { value: 'ru-RU', label: 'Russian' },
];

export default function UsersPage() {
  const queryClient = useQueryClient();

  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const canCreate = useHasPermission(PERM.USER_CREATE);
  const canUpdate = useHasPermission(PERM.USER_UPDATE);
  const canDelete = useHasPermission(PERM.USER_DELETE);
  const currentUserId = useAuthStore((s) => s.user?.id);

  // Fetch users. includeInactive=true so the Status filter's "Inactive"/"All"
  // options have data to show -- the backend excludes inactive users by default.
  const {
    data: users = [],
    isLoading,
    isError: usersError,
    refetch: refetchUsers,
  } = useQuery({
    queryKey: ['users'],
    queryFn: () => apiClient.getUsers(true),
  });

  // Fetch available roles, used only to populate the Role filter dropdown.
  // Viewing this page only requires user.read -- role.read is a separate,
  // narrower permission (e.g. RESTAURANT_MANAGER has the former, not the
  // latter) -- so a failure here must not block the users list itself; it
  // just means the Role filter degrades to "All Roles" (H-fix).
  const {
    data: roles = [],
    isError: rolesError,
    error: rolesQueryError,
    refetch: refetchRoles,
  } = useQuery({
    queryKey: ['roles'],
    queryFn: () => apiClient.getRoles(),
  });

  const rolesErrorMessage =
    (rolesQueryError as any)?.response?.data?.message ||
    'Failed to load roles -- the Role filter will be unavailable.';

  const hasPageError = usersError;
  const retryPageQueries = () => {
    refetchUsers();
    refetchRoles();
  };

  const roleFilterOptions = useMemo(
    () => [
      { label: 'All Roles', value: 'all' },
      ...roles
        .filter((r: Role) => r.active !== false)
        .map((r: Role) => ({ label: formatRoleLabel(r), value: r.name })),
    ],
    [roles],
  );

  // Filter users
  const filteredUsers = useMemo(() => {
    return users.filter((user: User) => {
      const matchesSearch =
        searchQuery === '' ||
        user.username.toLowerCase().includes(searchQuery.toLowerCase()) ||
        user.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (user.firstName &&
          user.firstName.toLowerCase().includes(searchQuery.toLowerCase())) ||
        (user.lastName &&
          user.lastName.toLowerCase().includes(searchQuery.toLowerCase()));

      const matchesRole =
        roleFilter === 'all' ||
        user.roles?.some((role) => role.name === roleFilter);

      const matchesStatus =
        statusFilter === 'all' ||
        (statusFilter === 'active' && user.active) ||
        (statusFilter === 'inactive' && !user.active);

      return matchesSearch && matchesRole && matchesStatus;
    });
  }, [users, searchQuery, roleFilter, statusFilter]);

  const openCreateModal = () => {
    setEditingUser(null);
    setIsModalOpen(true);
  };

  const openEditModal = (user: User) => {
    setEditingUser(user);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setEditingUser(null);
    setIsModalOpen(false);
  };

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (userId: number) => apiClient.deleteUser(userId),
    onSuccess: () => {
      setActionError(null);
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
    onError: (error: any) => {
      setActionError(error.response?.data?.message || 'Failed to delete user');
    },
  });

  const handleDelete = async (userId: number) => {
    if (
      window.confirm(
        'Are you sure you want to delete this user? This action cannot be undone.'
      )
    ) {
      try {
        await deleteMutation.mutateAsync(userId);
      } catch {
        // onError already populated actionError
      }
    }
  };

  // Reactivate mutation — flips `active` back to true via the existing update
  // endpoint (there is no dedicated reactivate endpoint). Only `email`/`active`
  // are sent; every other field is left untouched server-side since the DTO's
  // other fields are null and updateUser only applies non-null fields.
  const reactivateMutation = useMutation({
    mutationFn: (targetUser: User) =>
      apiClient.updateUser(targetUser.id, {
        email: targetUser.email,
        active: true,
      }),
    onSuccess: () => {
      setActionError(null);
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
    onError: (error: any) => {
      setActionError(error.response?.data?.message || 'Failed to reactivate user');
    },
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading users...</div>
      </div>
    );
  }

  if (hasPageError) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-3 text-center">
        <AlertTriangle className="h-8 w-8 text-red-500" />
        <div className="text-gray-900 font-medium">Failed to load users</div>
        <button onClick={retryPageQueries} className="btn btn-outline h-10">
          <RefreshCw className="h-4 w-4 mr-2" />
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">User Management</h1>
          <p className="mt-1 text-sm text-gray-500">
            Manage system users and access
          </p>
        </div>

        {canCreate && (
          <button onClick={openCreateModal} className="btn btn-primary h-10">
            <Plus className="h-4 w-4 mr-2" />
            Add User
          </button>
        )}
      </div>

      {actionError && (
        <div
          role="alert"
          className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
        >
          {actionError}
        </div>
      )}

      {/* Search and Filters */}
      <div className="card p-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="md:col-span-2">
            <SearchBar
              value={searchQuery}
              onChange={setSearchQuery}
              placeholder="Search by name, username, or email..."
            />
          </div>
          <div>
            <FilterDropdown
              label="Role"
              value={roleFilter}
              onChange={setRoleFilter}
              options={roleFilterOptions}
            />
            {rolesError && (
              <p className="mt-1 text-xs text-amber-600" role="alert">
                {rolesErrorMessage}
              </p>
            )}
          </div>
          <FilterDropdown
            label="Status"
            value={statusFilter}
            onChange={setStatusFilter}
            options={[
              { label: 'All Users', value: 'all' },
              { label: 'Active', value: 'active' },
              { label: 'Inactive', value: 'inactive' },
            ]}
          />
        </div>

        <div className="mt-3 text-sm text-gray-600">
          Showing {filteredUsers.length} of {users.length} users
        </div>
      </div>

      {/* Users Table */}
      <div className="card overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                User
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Contact
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Roles
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredUsers.map((user) => (
              <tr key={user.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <div className="flex-shrink-0 h-10 w-10 bg-primary-100 rounded-full flex items-center justify-center">
                      <span className="text-primary-700 font-semibold">
                        {user.firstName?.[0] || user.username[0].toUpperCase()}
                      </span>
                    </div>
                    <div className="ml-4">
                      <div className="text-sm font-medium text-gray-900">
                        {user.firstName && user.lastName
                          ? `${user.firstName} ${user.lastName}`
                          : user.username}
                      </div>
                      <div className="text-sm text-gray-500">
                        @{user.username}
                      </div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900">
                    <div className="flex items-center">
                      <Mail className="h-4 w-4 mr-2 text-gray-400" />
                      {user.email}
                    </div>
                    {user.phone && (
                      <div className="flex items-center mt-1">
                        <Phone className="h-4 w-4 mr-2 text-gray-400" />
                        {user.phone}
                      </div>
                    )}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex flex-wrap gap-1">
                    {user.roles?.map((assignment) => (
                      <span
                        key={assignment.assignmentId ?? `${assignment.id}-${assignment.contextType ?? ''}-${assignment.restaurantGroupId ?? ''}-${assignment.restaurantId ?? ''}`}
                        className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-primary-100 text-primary-700"
                      >
                        <Shield className="h-3 w-3 mr-1" />
                        {formatAssignmentLabel(assignment)}
                      </span>
                    ))}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span
                    className={`badge ${
                      user.active ? 'badge-success' : 'badge-gray'
                    }`}
                  >
                    {user.active ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  {canUpdate && (
                    <button
                      onClick={() => openEditModal(user)}
                      className="text-primary-600 hover:text-primary-900 hover:bg-primary-50 rounded-md p-2 min-h-[44px] min-w-[44px] inline-flex items-center justify-center mr-1"
                      aria-label={`Edit ${user.username}`}
                    >
                      <Edit2 className="h-5 w-5" />
                    </button>
                  )}
                  {canUpdate && !user.active && (
                    <button
                      onClick={() => reactivateMutation.mutate(user)}
                      disabled={reactivateMutation.isPending}
                      className="text-green-600 hover:text-green-900 hover:bg-green-50 rounded-md p-2 min-h-[44px] min-w-[44px] inline-flex items-center justify-center mr-1"
                      aria-label={`Reactivate ${user.username}`}
                    >
                      <RotateCcw className="h-5 w-5" />
                    </button>
                  )}
                  {canDelete && (
                    <button
                      onClick={() => handleDelete(user.id)}
                      disabled={user.id === currentUserId}
                      className="text-red-600 hover:text-red-900 hover:bg-red-50 rounded-md p-2 min-h-[44px] min-w-[44px] inline-flex items-center justify-center disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent"
                      aria-label={
                        user.id === currentUserId
                          ? 'You cannot delete your own account'
                          : `Delete ${user.username}`
                      }
                      title={
                        user.id === currentUserId
                          ? 'You cannot delete your own account'
                          : undefined
                      }
                    >
                      <Trash2 className="h-5 w-5" />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {filteredUsers.length === 0 && (
        <div className="card p-12 text-center">
          <p className="text-gray-500">
            {searchQuery || roleFilter !== 'all' || statusFilter !== 'all'
              ? 'No users match your filters'
              : 'No users found'}
          </p>
          {(searchQuery || roleFilter !== 'all' || statusFilter !== 'all') && (
            <button
              onClick={() => {
                setSearchQuery('');
                setRoleFilter('all');
                setStatusFilter('all');
              }}
              className="btn btn-outline mt-4 h-10"
            >
              Clear Filters
            </button>
          )}
        </div>
      )}

      {/* Create/Edit Modal */}
      {isModalOpen && (
        <UserModal user={editingUser} currentUserId={currentUserId} onClose={closeModal} onSuccess={() => {
          queryClient.invalidateQueries({ queryKey: ['users'] });
          closeModal();
        }} />
      )}
    </div>
  );
}

// User Modal Component
interface UserModalProps {
  user: User | null;
  currentUserId: number | undefined;
  onClose: () => void;
  onSuccess: () => void;
}

/**
 * Translate a UserRoleAssignment (server-side shape) into a RoleAssignment
 * (request shape) so edit-mode can pre-populate the form.
 */
function toAssignment(a: UserRoleAssignment): RoleAssignment {
  return {
    roleId: a.id,
    contextType: a.contextType ?? 'PLATFORM',
    restaurantGroupId: a.restaurantGroupId ?? null,
    restaurantId: a.restaurantId ?? null,
  };
}

function UserModal({ user, currentUserId, onClose, onSuccess }: UserModalProps) {
  const isSelf = user != null && user.id === currentUserId;
  const [formData, setFormData] = useState({
    username: user?.username || '',
    email: user?.email || '',
    password: '',
    firstName: user?.firstName || '',
    lastName: user?.lastName || '',
    phone: user?.phone || '',
    timezone: user?.timezone || '',
    locale: user?.locale || '',
    active: user?.active ?? true,
    assignments: (user?.roles ?? []).map(toAssignment) as RoleAssignment[],
  });
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [usernameTaken, setUsernameTaken] = useState(false);
  const [emailTaken, setEmailTaken] = useState(false);

  // Live existence checks (L3) -- apiClient already exposes the endpoints
  // (used by RolesPage's checkRoleNameExists), so mirror that pattern instead
  // of only catching a duplicate after a full submit round-trip.
  const checkUsernameAvailability = async () => {
    const username = formData.username;
    if (user || !username || username.length < 3) {
      setUsernameTaken(false);
      return;
    }
    try {
      setUsernameTaken(await apiClient.checkUsernameExists(username));
    } catch {
      setUsernameTaken(false); // backend re-validates on submit anyway
    }
  };

  const checkEmailAvailability = async () => {
    const email = formData.email;
    if (!email || email === user?.email) {
      setEmailTaken(false);
      return;
    }
    try {
      setEmailTaken(await apiClient.checkEmailExists(email));
    } catch {
      setEmailTaken(false); // backend re-validates on submit anyway
    }
  };

  const { data: roles = [], isError: rolesError } = useQuery({
    queryKey: ['roles'],
    queryFn: () => apiClient.getRoles(),
  });

  // Mirrors the page-level Role filter, which deliberately excludes inactive
  // roles (M2) -- an admin shouldn't be able to newly assign a deactivated
  // role that's hidden everywhere else in the UI.
  const activeRoles = useMemo(() => roles.filter((r) => r.active !== false), [roles]);

  const { data: groups = [], isError: groupsError } = useQuery<RestaurantGroup[]>({
    queryKey: ['restaurant-groups'],
    queryFn: () => apiClient.getGroups(),
  });

  // Restaurants API is paginated with no picker-friendly "give me everything"
  // option, so walk every page instead of hardcoding a page size that would
  // silently truncate the list past that many restaurants.
  const { data: restaurants = [], isError: restaurantsError } = useQuery<Restaurant[]>({
    queryKey: ['restaurants', 'all-for-user-modal'],
    queryFn: async () => {
      const all: Restaurant[] = [];
      let page = 0;
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const result = await apiClient.getRestaurants(page, 200);
        all.push(...result.content);
        if (result.last) break;
        page += 1;
      }
      return all;
    },
  });

  const updateAssignment = (index: number, patch: Partial<RoleAssignment>) => {
    setFormData((prev) => {
      const next = [...prev.assignments];
      next[index] = { ...next[index], ...patch };
      return { ...prev, assignments: next };
    });
  };

  const addAssignment = () => {
    const firstRole = activeRoles[0];
    if (!firstRole) return;
    const ctx: ContextType = (firstRole.roleLevel ?? 'PLATFORM') as ContextType;
    setFormData((prev) => ({
      ...prev,
      assignments: [
        ...prev.assignments,
        {
          roleId: firstRole.id,
          contextType: ctx,
          restaurantGroupId: null,
          restaurantId: null,
        },
      ],
    }));
  };

  const removeAssignment = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      assignments: prev.assignments.filter((_, i) => i !== index),
    }));
  };

  const validateAssignments = (): string | null => {
    const seen = new Map<string, number>();
    for (let i = 0; i < formData.assignments.length; i++) {
      const a = formData.assignments[i];
      if (a.contextType === 'GROUP' && !a.restaurantGroupId) {
        return `Row ${i + 1}: select a group for GROUP-context role.`;
      }
      if (a.contextType === 'RESTAURANT' && !a.restaurantId) {
        return `Row ${i + 1}: select a restaurant for RESTAURANT-context role.`;
      }

      // Matches the backend's (user_id, role_id, restaurant_group_id, restaurant_id)
      // unique constraint -- catch the duplicate here with a row number instead
      // of letting it hit the DB and come back as an opaque "already exists" (L2).
      const key = `${a.roleId}|${a.contextType}|${a.restaurantGroupId ?? ''}|${a.restaurantId ?? ''}`;
      const firstRow = seen.get(key);
      if (firstRow !== undefined) {
        return `Row ${i + 1}: duplicates row ${firstRow + 1} (same role, context, and scope).`;
      }
      seen.set(key, i);
    }
    return null;
  };

  const createMutation = useMutation({
    mutationFn: (data: UserCreateRequest) => apiClient.createUser(data),
    onSuccess,
    onError: (error: any) => {
      const message = error.response?.data?.message || 'Failed to create user';
      setSubmitError(message);
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: UserUpdateRequest) => apiClient.updateUser(user!.id, data),
    onSuccess,
    onError: (error: any) => {
      const message = error.response?.data?.message || 'Failed to update user';
      setSubmitError(message);
    },
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);

    if (usernameTaken || emailTaken) {
      return;
    }

    const validationError = validateAssignments();
    if (validationError) {
      setSubmitError(validationError);
      return;
    }

    // Deactivating has no separate confirm step elsewhere in this form (unlike
    // the page-level Delete button), so an admin fixing an unrelated field
    // could untick "Active user" and save without realising it locks the
    // account out the same way Delete does (H2).
    if (user?.active && !formData.active) {
      const confirmed = window.confirm(
        `Deactivate ${user.username}? They will disappear from the active user list and lose access immediately. This can be undone with the Reactivate action.`
      );
      if (!confirmed) {
        return;
      }
    }

    // Normalise assignment payload — null out FKs that don't apply to the chosen context.
    const normalisedAssignments: RoleAssignment[] = formData.assignments.map((a) => ({
      roleId: a.roleId,
      contextType: a.contextType,
      restaurantGroupId: a.contextType === 'GROUP' ? a.restaurantGroupId ?? null : null,
      restaurantId: a.contextType === 'RESTAURANT' ? a.restaurantId ?? null : null,
    }));

    if (user) {
      const updateData: UserUpdateRequest = {
        email: formData.email,
        firstName: formData.firstName || undefined,
        lastName: formData.lastName || undefined,
        phone: formData.phone || undefined,
        timezone: formData.timezone || undefined,
        locale: formData.locale || undefined,
        active: formData.active,
        assignments: normalisedAssignments,
        ...(formData.password ? { password: formData.password } : {}),
      };
      try {
        await updateMutation.mutateAsync(updateData);
      } catch {
        // onError already populated submitError
      }
    } else {
      const createData: UserCreateRequest = {
        username: formData.username,
        email: formData.email,
        password: formData.password,
        firstName: formData.firstName || undefined,
        lastName: formData.lastName || undefined,
        phone: formData.phone || undefined,
        timezone: formData.timezone || undefined,
        locale: formData.locale || undefined,
        active: formData.active,
        assignments: normalisedAssignments,
      };
      try {
        await createMutation.mutateAsync(createData);
      } catch {
        // onError already populated submitError
      }
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      onClick={onClose}
    >
      <div
        className="card max-w-2xl w-full mx-4 p-6 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-gray-900">
            {user ? 'Edit User' : 'Create User'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-md p-2 min-h-[44px] min-w-[44px] flex items-center justify-center"
            aria-label="Close modal"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {submitError && (
            <div
              role="alert"
              className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
            >
              {submitError}
            </div>
          )}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="user-firstname" className="block text-sm font-medium text-gray-700 mb-1">
                First Name
              </label>
              <input
                id="user-firstname"
                type="text"
                value={formData.firstName}
                onChange={(e) =>
                  setFormData({ ...formData, firstName: e.target.value })
                }
                className="input w-full"
              />
            </div>

            <div>
              <label htmlFor="user-lastname" className="block text-sm font-medium text-gray-700 mb-1">
                Last Name
              </label>
              <input
                id="user-lastname"
                type="text"
                value={formData.lastName}
                onChange={(e) =>
                  setFormData({ ...formData, lastName: e.target.value })
                }
                className="input w-full"
              />
            </div>
          </div>

          <div>
            <label htmlFor="user-username" className="block text-sm font-medium text-gray-700 mb-1">
              Username *
            </label>
            <input
              id="user-username"
              type="text"
              value={formData.username}
              onChange={(e) => {
                setUsernameTaken(false);
                setFormData({ ...formData, username: e.target.value });
              }}
              onBlur={checkUsernameAvailability}
              className="input w-full"
              required
              disabled={!!user}
            />
            {usernameTaken && (
              <p className="mt-1 text-sm text-red-600" role="alert">
                A user with this username already exists.
              </p>
            )}
          </div>

          <div>
            <label htmlFor="user-email" className="block text-sm font-medium text-gray-700 mb-1">
              Email *
            </label>
            <input
              id="user-email"
              type="email"
              value={formData.email}
              onChange={(e) => {
                setEmailTaken(false);
                setFormData({ ...formData, email: e.target.value });
              }}
              onBlur={checkEmailAvailability}
              className="input w-full"
              required
            />
            {emailTaken && (
              <p className="mt-1 text-sm text-red-600" role="alert">
                A user with this email already exists.
              </p>
            )}
          </div>

          <div>
            <label htmlFor="user-phone" className="block text-sm font-medium text-gray-700 mb-1">
              Phone
            </label>
            <input
              id="user-phone"
              type="tel"
              value={formData.phone}
              onChange={(e) =>
                setFormData({ ...formData, phone: e.target.value })
              }
              className="input w-full"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="user-timezone" className="block text-sm font-medium text-gray-700 mb-1">
                Timezone
              </label>
              <select
                id="user-timezone"
                value={formData.timezone}
                onChange={(e) => setFormData({ ...formData, timezone: e.target.value })}
                className="input w-full"
              >
                <option value="">— Not set —</option>
                {TIMEZONE_OPTIONS.map((tz) => (
                  <option key={tz} value={tz}>
                    {tz}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label htmlFor="user-locale" className="block text-sm font-medium text-gray-700 mb-1">
                Locale
              </label>
              <select
                id="user-locale"
                value={formData.locale}
                onChange={(e) => setFormData({ ...formData, locale: e.target.value })}
                className="input w-full"
              >
                <option value="">— Not set —</option>
                {LOCALE_OPTIONS.map((l) => (
                  <option key={l.value} value={l.value}>
                    {l.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label htmlFor="user-password" className="block text-sm font-medium text-gray-700 mb-1">
              Password {user ? '(leave blank to keep unchanged)' : '*'}
            </label>
            <input
              id="user-password"
              type="password"
              value={formData.password}
              onChange={(e) =>
                setFormData({ ...formData, password: e.target.value })
              }
              className="input w-full"
              required={!user}
              minLength={8}
              maxLength={100}
              title="Password must be between 8 and 100 characters"
            />
            <p className="mt-1 text-xs text-gray-500">Must be 8-100 characters.</p>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-gray-700">
                Role assignments
              </label>
              <button
                type="button"
                onClick={addAssignment}
                className="text-sm text-primary-600 hover:text-primary-800"
                disabled={activeRoles.length === 0}
              >
                + Add role assignment
              </button>
            </div>
            {(rolesError || groupsError || restaurantsError) && (
              <p className="mb-2 text-xs text-amber-600" role="alert">
                {rolesError
                  ? 'Failed to load roles -- role assignment is unavailable right now.'
                  : 'Failed to load ' +
                    [groupsError && 'groups', restaurantsError && 'restaurants']
                      .filter(Boolean)
                      .join(' and ') +
                    ' -- the corresponding picker below will be empty.'}
              </p>
            )}
            {formData.assignments.length === 0 ? (
              <p className="text-sm text-gray-500 italic">
                No roles assigned. Use "+ Add role assignment" to grant the user a role.
              </p>
            ) : (
              <div className="space-y-3">
                {formData.assignments.map((a, idx) => {
                  const role = roles.find((r) => r.id === a.roleId);
                  // Keep the currently-assigned role selectable even if it's since
                  // been deactivated, so an existing assignment doesn't silently
                  // change out from under the admin -- just don't offer it for
                  // *new* selections (it's excluded from activeRoles above).
                  const roleOptions =
                    role && role.active === false ? [role, ...activeRoles] : activeRoles;
                  return (
                    <div key={idx} className="border border-gray-200 rounded-md p-3 space-y-2">
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">
                            Role
                          </label>
                          <select
                            value={a.roleId}
                            onChange={(e) => {
                              const nextRole = roles.find((r) => r.id === Number(e.target.value));
                              const nextCtx = (nextRole?.roleLevel ?? a.contextType) as ContextType;
                              updateAssignment(idx, {
                                roleId: Number(e.target.value),
                                contextType: nextCtx,
                                restaurantGroupId: null,
                                restaurantId: null,
                              });
                            }}
                            className="input w-full"
                          >
                            {roleOptions.map((r) => (
                              <option key={r.id} value={r.id}>
                                {formatRoleLabel(r)}
                                {r.active === false ? ' (inactive)' : ''}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">
                            Context
                          </label>
                          <select
                            value={a.contextType}
                            onChange={(e) =>
                              updateAssignment(idx, {
                                contextType: e.target.value as ContextType,
                                restaurantGroupId: null,
                                restaurantId: null,
                              })
                            }
                            className="input w-full"
                          >
                            <option value="PLATFORM">Platform</option>
                            <option value="GROUP">Group</option>
                            <option value="RESTAURANT">Restaurant</option>
                          </select>
                        </div>
                      </div>
                      {a.contextType === 'GROUP' && (
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">
                            Group
                          </label>
                          <select
                            value={a.restaurantGroupId ?? ''}
                            onChange={(e) =>
                              updateAssignment(idx, {
                                restaurantGroupId: e.target.value ? Number(e.target.value) : null,
                              })
                            }
                            className="input w-full"
                          >
                            <option value="">— Select a group —</option>
                            {groups.map((g) => (
                              <option key={g.id} value={g.id}>
                                {g.name}
                              </option>
                            ))}
                          </select>
                        </div>
                      )}
                      {a.contextType === 'RESTAURANT' && (
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">
                            Restaurant
                          </label>
                          <select
                            value={a.restaurantId ?? ''}
                            onChange={(e) =>
                              updateAssignment(idx, {
                                restaurantId: e.target.value ? Number(e.target.value) : null,
                              })
                            }
                            className="input w-full"
                          >
                            <option value="">— Select a restaurant —</option>
                            {restaurants.map((r) => (
                              <option key={r.id} value={r.id}>
                                {r.name}
                              </option>
                            ))}
                          </select>
                        </div>
                      )}
                      <div className="flex items-center justify-between text-xs text-gray-500">
                        <span>
                          {role
                            ? `${formatRoleLabel(role)} · ${a.contextType.toLowerCase()}`
                            : 'Unknown role'}
                        </span>
                        <button
                          type="button"
                          onClick={() => removeAssignment(idx)}
                          className="text-red-600 hover:text-red-800"
                          aria-label={`Remove assignment ${idx + 1}`}
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div>
            <div className="flex items-center">
              <input
                type="checkbox"
                id="active"
                checked={formData.active}
                disabled={isSelf}
                onChange={(e) =>
                  setFormData({ ...formData, active: e.target.checked })
                }
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded disabled:opacity-50"
              />
              <label htmlFor="active" className="ml-2 text-sm text-gray-700">
                Active user
              </label>
            </div>
            {isSelf && (
              <p className="mt-1 text-xs text-gray-500">
                You cannot deactivate your own account.
              </p>
            )}
          </div>

          <div className="flex space-x-3 pt-4 border-t border-gray-200">
            <button type="button" onClick={onClose} className="btn btn-outline flex-1 h-10">
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary flex-1 h-10"
              disabled={createMutation.isPending || updateMutation.isPending}
            >
              {createMutation.isPending || updateMutation.isPending
                ? 'Saving...'
                : user
                ? 'Update User'
                : 'Create User'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
